import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, TextField, Button, IconButton, 
  List, ListItem, ListItemText, Avatar, Chip,
  Dialog, DialogTitle, DialogContent,
  Collapse, Alert, CircularProgress, useTheme, useMediaQuery,
  Card, CardContent, Grid, LinearProgress, Divider
} from '@mui/material';
import {
  Send as SendIcon, SmartToy as AIIcon, Person as PersonIcon,
  Lightbulb as LightbulbIcon, Close as CloseIcon, 
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon, Schedule as ScheduleIcon,
  Psychology as PsychologyIcon, Assessment as AssessmentIcon
} from '@mui/icons-material';
import { FaBrain, FaChartLine, FaBullseye, FaClock, FaTrophy, FaExclamationTriangle } from 'react-icons/fa';
import api from '../api';

const EnhancedAITradingChat = ({ user, isOpen, onClose, userPerformance }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedInsights, setExpandedInsights] = useState({});
  const [aiPersonality, setAiPersonality] = useState('expert');
  const [tradingData, setTradingData] = useState(null);
  const [marketInsights, setMarketInsights] = useState(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // AI Personality options with enhanced capabilities
  const aiPersonalities = {
    expert: {
      name: 'Trading Expert',
      description: 'Advanced pattern recognition and market analysis',
      icon: <FaChartLine />,
      color: 'primary',
      specialty: 'Technical Analysis & Pattern Recognition'
    },
    psychologist: {
      name: 'Psychology Coach',
      description: 'Emotional control and mindset optimization',
      icon: <FaBrain />,
      color: 'secondary',
      specialty: 'Trading Psychology & Emotional Control'
    },
    strategist: {
      name: 'Strategy Master',
      description: 'Long-term planning and portfolio optimization',
      icon: <FaBullseye />,
      color: 'success',
      specialty: 'Risk Management & Portfolio Strategy'
    },
    analyst: {
      name: 'Data Analyst',
      description: 'Deep data analysis and profitable time patterns',
      icon: <FaClock />,
      color: 'info',
      specialty: 'Data Analysis & Time-Based Patterns'
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTradingData();
      initializeChat();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchTradingData = async () => {
    try {
      setIsLoading(true); // Fixed: Changed setLoading to setIsLoading
      setError(null);
      
      // Use the existing analytics endpoint
      const response = await api.get('/api/analytics/trades/analytics');
      const [tradesResponse, psychologyResponse] = await Promise.allSettled([
        api.get('/api/trades'),
        api.get('/api/psychology/analysis')
      ]);

      
      if (response.data) {
        setTradingData(response.data);
      } else {
        setTradingData(null);
      }
    } catch (err) {
      console.error('Failed to fetch trading data:', err);
      setError('Failed to load trading data. Please try again.');
      setTradingData(null);
    } finally {
      setIsLoading(false); // Fixed: Changed setLoading to setIsLoading
    }
  };

  const analyzeProfitableTimes = (trades) => {
    if (!trades || trades.length === 0) return null;

    const timeSlots = {
      '9:00-11:00': { trades: 0, wins: 0, totalPnL: 0, avgPnL: 0 },
      '11:00-13:00': { trades: 0, wins: 0, totalPnL: 0, avgPnL: 0 },
      '13:00-15:00': { trades: 0, wins: 0, totalPnL: 0, avgPnL: 0 },
      '15:00-17:00': { trades: 0, wins: 0, totalPnL: 0, avgPnL: 0 },
      '17:00-19:00': { trades: 0, wins: 0, totalPnL: 0, avgPnL: 0 },
      '19:00-21:00': { trades: 0, wins: 0, totalPnL: 0, avgPnL: 0 }
    };

    trades.forEach(trade => {
      const hour = new Date(trade.openDate).getHours();
      let timeSlot = '';
      
      if (hour >= 9 && hour < 11) timeSlot = '9:00-11:00';
      else if (hour >= 11 && hour < 13) timeSlot = '11:00-13:00';
      else if (hour >= 13 && hour < 15) timeSlot = '13:00-15:00';
      else if (hour >= 15 && hour < 17) timeSlot = '15:00-17:00';
      else if (hour >= 17 && hour < 19) timeSlot = '17:00-19:00';
      else if (hour >= 19 && hour < 21) timeSlot = '19:00-21:00';

      if (timeSlot && timeSlots[timeSlot]) {
        timeSlots[timeSlot].trades++;
        timeSlots[timeSlot].totalPnL += trade.pnl || 0;
        if (trade.pnl > 0) timeSlots[timeSlot].wins++;
      }
    });

    // Calculate averages and find most profitable times
    Object.keys(timeSlots).forEach(slot => {
      if (timeSlots[slot].trades > 0) {
        timeSlots[slot].avgPnL = timeSlots[slot].totalPnL / timeSlots[slot].trades;
        timeSlots[slot].winRate = timeSlots[slot].wins / timeSlots[slot].trades;
      }
    });

    // Sort by profitability
    const sortedSlots = Object.entries(timeSlots)
      .filter(([_, data]) => data.trades > 0)
      .sort((a, b) => b[1].avgPnL - a[1].avgPnL);

    return {
      timeSlots,
      mostProfitable: sortedSlots[0],
      leastProfitable: sortedSlots[sortedSlots.length - 1],
      overallPattern: sortedSlots
    };
  };

  const analyzeTradingPatterns = (userTrades, allTraders) => {
    if (!userTrades || userTrades.length === 0) return null;

    // User's best and worst trades
    const userBestTrades = userTrades
      .filter(t => t.pnl > 0)
      .sort((a, b) => b.pnl - a.pnl)
      .slice(0, 3);

    const userWorstTrades = userTrades
      .filter(t => t.pnl < 0)
      .sort((a, b) => a.pnl - b.pnl)
      .slice(0, 3);

    // Collective wisdom from all traders
    const collectiveBest = allTraders?.bestTrades || [];
    const collectiveWorst = allTraders?.worstTrades || [];

    // Pattern analysis
    const patterns = {
      userBest: userBestTrades.map(t => ({
        symbol: t.symbol,
        pnl: t.pnl,
        date: t.openDate,
        side: t.side,
        entryTime: new Date(t.openDate).getHours(),
        holdingTime: calculateHoldingTime(t.openDate, t.closeDate)
      })),
      userWorst: userWorstTrades.map(t => ({
        symbol: t.symbol,
        pnl: t.pnl,
        date: t.openDate,
        side: t.side,
        entryTime: new Date(t.openDate).getHours(),
        holdingTime: calculateHoldingTime(t.openDate, t.closeDate)
      })),
      collectiveBest: collectiveBest.slice(0, 5),
      collectiveWorst: collectiveWorst.slice(0, 5),
      commonPatterns: identifyCommonPatterns(userTrades, allTraders)
    };

    return patterns;
  };

  const calculateHoldingTime = (openDate, closeDate) => {
    if (!closeDate) return 'Open';
    const open = new Date(openDate);
    const close = new Date(closeDate);
    const diffHours = (close - open) / (1000 * 60 * 60);
    
    if (diffHours < 1) return `${Math.round(diffHours * 60)}m`;
    if (diffHours < 24) return `${Math.round(diffHours)}h`;
    return `${Math.round(diffHours / 24)}d`;
  };

  const identifyCommonPatterns = (userTrades, allTraders) => {
    const patterns = {
      highWinRateSymbols: [],
      highLossRateSymbols: [],
      bestEntryTimes: [],
      worstEntryTimes: [],
      profitableHoldingPeriods: []
    };

    // Analyze user's patterns
    if (userTrades && userTrades.length > 0) {
      const symbolStats = {};
      userTrades.forEach(trade => {
        if (!symbolStats[trade.symbol]) {
          symbolStats[trade.symbol] = { trades: 0, wins: 0, totalPnL: 0 };
        }
        symbolStats[trade.symbol].trades++;
        symbolStats[trade.symbol].totalPnL += trade.pnl || 0;
        if (trade.pnl > 0) symbolStats[trade.symbol].wins++;
      });

      // Find best and worst symbols
      Object.entries(symbolStats).forEach(([symbol, stats]) => {
        const winRate = stats.wins / stats.trades;
        if (winRate >= 0.7) patterns.highWinRateSymbols.push({ symbol, winRate, avgPnL: stats.totalPnL / stats.trades });
        if (winRate <= 0.3) patterns.highLossRateSymbols.push({ symbol, winRate, avgPnL: stats.totalPnL / stats.trades });
      });
    }

    return patterns;
  };

  const generateMarketInsights = (allTraders) => {
    if (!allTraders) return null;

    const insights = {
      topPerformingSymbols: allTraders.topSymbols || [],
      marketSentiment: allTraders.sentiment || 'Neutral',
      volatilityTrend: allTraders.volatility || 'Medium',
      sectorPerformance: allTraders.sectors || [],
      collectiveWinRate: allTraders.overallWinRate || 0.55
    };

    return insights;
  };

  const initializeChat = () => {
    const welcomeMessage = {
      id: Date.now(),
      type: 'ai',
      content: `Hello ${user?.email}! I'm your Enhanced AI Trading Assistant. I've analyzed your trading data and learned from collective trader insights to provide you with advanced, personalized advice.

🔍 **What I've Learned About Your Trading:**
• Your trading patterns and performance metrics
• Most profitable time slots and entry strategies
• Best and worst trade patterns
• Collective wisdom from successful traders

💡 **Ask me about:**
• Your most profitable trading times
• Pattern analysis of your best/worst trades
• Risk management based on your data
• Market insights from collective trader data
• Psychology optimization for your trading style

Choose your preferred AI personality above, or start asking questions!`,
      timestamp: new Date(),
      insights: {
        performance: userPerformance?.overall || 'Good',
        riskLevel: userPerformance?.riskLevel || 'Medium',
        psychologyScore: userPerformance?.psychologyScore || 70
      }
    };
    setMessages([welcomeMessage]);
    setInputMessage('');
    setError(null);
    
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError(null);

    try {
      const aiResponse = await generateEnhancedAIResponse(inputMessage.trim());
      
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: aiResponse.content,
        timestamp: new Date(),
        insights: aiResponse.insights,
        recommendations: aiResponse.recommendations,
        dataPoints: aiResponse.dataPoints,
        charts: aiResponse.charts
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      console.error('AI response error:', err);
      setError('Failed to get AI response. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const generateEnhancedAIResponse = async (userInput) => {
    await new Promise(resolve => setTimeout(resolve, 1500));

    const input = userInput.toLowerCase();
    const personality = aiPersonalities[aiPersonality];

    let response = {
      content: '',
      insights: {},
      recommendations: [],
      dataPoints: [],
      charts: null
    };

    if (input.includes('time') || input.includes('when') || input.includes('hour')) {
      response = generateTimeAnalysisResponse(personality);
    } else if (input.includes('best') || input.includes('winning') || input.includes('profitable')) {
      response = generateBestTradesResponse(personality);
    } else if (input.includes('worst') || input.includes('losing') || input.includes('loss')) {
      response = generateWorstTradesResponse(personality);
    } else if (input.includes('pattern') || input.includes('analysis') || input.includes('trend')) {
      response = generatePatternAnalysisResponse(personality);
    } else if (input.includes('market') || input.includes('collective') || input.includes('other')) {
      response = generateMarketInsightsResponse(personality);
    } else if (input.includes('psychology') || input.includes('mindset') || input.includes('emotion')) {
      response = generatePsychologyResponse(personality);
    } else if (input.includes('risk') || input.includes('management') || input.includes('position')) {
      response = generateRiskResponse(personality);
    } else {
      response = generateGeneralResponse(personality, userInput);
    }

    return response;
  };

  const generateTimeAnalysisResponse = (personality) => {
    if (!tradingData?.timeAnalysis) {
      return {
        content: `⏰ **Time Analysis - ${personality.name}**\n\nI don't have enough trading data to analyze your profitable time patterns yet. Complete at least 10 trades to get personalized time-based insights.`,
        insights: { dataAvailable: false },
        recommendations: ['Complete more trades to enable time pattern analysis', 'Track your entry times consistently', 'Analyze your performance by hour'],
        dataPoints: []
      };
    }

    const { mostProfitable, leastProfitable, overallPattern } = tradingData.timeAnalysis;
    
    let content = `⏰ **Profitable Time Analysis - ${personality.name}**\n\n`;
    content += `🎯 **Your Most Profitable Time: ${mostProfitable[0]}**\n`;
    content += `• Win Rate: ${(mostProfitable[1].winRate * 100).toFixed(1)}%\n`;
    content += `• Average P&L: $${mostProfitable[1].avgPnL.toFixed(2)}\n`;
    content += `• Total Trades: ${mostProfitable[1].trades}\n\n`;
    
    content += `⚠️ **Your Least Profitable Time: ${leastProfitable[0]}**\n`;
    content += `• Win Rate: ${(leastProfitable[1].winRate * 100).toFixed(1)}%\n`;
    content += `• Average P&L: $${leastProfitable[1].avgPnL.toFixed(2)}\n`;
    content += `• Total Trades: ${leastProfitable[1].trades}\n\n`;
    
    content += `📊 **Time Pattern Ranking (Most to Least Profitable):**\n`;
    overallPattern.forEach(([time, data], index) => {
      content += `${index + 1}. ${time}: $${data.avgPnL.toFixed(2)} avg, ${(data.winRate * 100).toFixed(1)}% win rate\n`;
    });

    return {
      content,
      insights: {
        mostProfitableTime: mostProfitable[0],
        leastProfitableTime: leastProfitable[0],
        timePatterns: overallPattern
      },
      recommendations: [
        `Focus your trading during ${mostProfitable[0]} - your most profitable window`,
        `Avoid or reduce position sizes during ${leastProfitable[0]}`,
        'Use time-based position sizing to optimize your performance',
        'Track your performance by hour to refine your timing strategy'
      ],
      dataPoints: [
        { label: 'Best Time', value: mostProfitable[0], unit: '' },
        { label: 'Best Win Rate', value: (mostProfitable[1].winRate * 100).toFixed(1), unit: '%' },
        { label: 'Best Avg P&L', value: `$${mostProfitable[1].avgPnL.toFixed(2)}`, unit: '' }
      ]
    };
  };

  const generateBestTradesResponse = (personality) => {
    if (!tradingData?.patternAnalysis?.userBest) {
      return {
        content: `🏆 **Best Trades Analysis - ${personality.name}**\n\nI need more trading data to analyze your best trades. Complete at least 5 winning trades to get detailed insights.`,
        insights: { dataAvailable: false },
        recommendations: ['Focus on completing more trades', 'Track your entry and exit reasons', 'Document your successful setups'],
        dataPoints: []
      };
    }

    const bestTrades = tradingData.patternAnalysis.userBest;
    
    let content = `🏆 **Your Best Trades Analysis - ${personality.name}**\n\n`;
    content += `**Top 3 Winning Trades:**\n`;
    bestTrades.forEach((trade, index) => {
      content += `${index + 1}. ${trade.symbol} (${trade.side}): $${trade.pnl.toFixed(2)} | ${trade.entryTime}:00 | ${trade.holdingTime}\n`;
    });
    
    content += `\n**Pattern Insights:**\n`;
    const entryTimes = bestTrades.map(t => t.entryTime);
    const avgEntryTime = entryTimes.reduce((a, b) => a + b, 0) / entryTimes.length;
    content += `• Average entry time: ${Math.round(avgEntryTime)}:00\n`;
    content += `• Most profitable symbol: ${bestTrades[0].symbol}\n`;
    content += `• Average holding time: ${bestTrades[0].holdingTime}\n`;

    return {
      content,
      insights: {
        bestTrades: bestTrades,
        avgEntryTime: Math.round(avgEntryTime),
        topSymbol: bestTrades[0].symbol
      },
      recommendations: [
        `Focus on ${bestTrades[0].symbol} - your most profitable symbol`,
        `Trade around ${Math.round(avgEntryTime)}:00 - your optimal entry time`,
        'Replicate the setups that led to your best trades',
        'Document your winning patterns for future reference'
      ],
      dataPoints: [
        { label: 'Best Trade P&L', value: `$${bestTrades[0].pnl.toFixed(2)}`, unit: '' },
        { label: 'Top Symbol', value: bestTrades[0].symbol, unit: '' },
        { label: 'Optimal Entry', value: `${Math.round(avgEntryTime)}:00`, unit: '' }
      ]
    };
  };

  const generateWorstTradesResponse = (personality) => {
    if (!tradingData?.patternAnalysis?.userWorst) {
      return {
        content: `⚠️ **Worst Trades Analysis - ${personality.name}**\n\nI need more trading data to analyze your losing trades. This analysis will help you avoid future losses.`,
        insights: { dataAvailable: false },
        recommendations: ['Complete more trades for pattern analysis', 'Document your losing setups', 'Focus on risk management'],
        dataPoints: []
      };
    }

    const worstTrades = tradingData.patternAnalysis.userWorst;
    
    let content = `⚠️ **Your Worst Trades Analysis - ${personality.name}**\n\n`;
    content += `**Top 3 Losing Trades:**\n`;
    worstTrades.forEach((trade, index) => {
      content += `${index + 1}. ${trade.symbol} (${trade.side}): $${trade.pnl.toFixed(2)} | ${trade.entryTime}:00 | ${trade.holdingTime}\n`;
    });
    
    content += `\n**Loss Pattern Analysis:**\n`;
    const entryTimes = worstTrades.map(t => t.entryTime);
    const avgEntryTime = entryTimes.reduce((a, b) => a + b, 0) / entryTimes.length;
    content += `• Average entry time: ${Math.round(avgEntryTime)}:00\n`;
    content += `• Most problematic symbol: ${worstTrades[0].symbol}\n`;
    content += `• Average holding time: ${worstTrades[0].holdingTime}\n`;

    return {
      content,
      insights: {
        worstTrades: worstTrades,
        avgEntryTime: Math.round(avgEntryTime),
        problematicSymbol: worstTrades[0].symbol
      },
      recommendations: [
        `Avoid trading ${worstTrades[0].symbol} until you improve your strategy`,
        `Be cautious around ${Math.round(avgEntryTime)}:00 - your problematic entry time`,
        'Review what went wrong in these trades',
        'Implement stricter stop-losses for these setups'
      ],
      dataPoints: [
        { label: 'Worst Loss', value: `$${worstTrades[0].pnl.toFixed(2)}`, unit: '' },
        { label: 'Problem Symbol', value: worstTrades[0].symbol, unit: '' },
        { label: 'Risky Entry Time', value: `${Math.round(avgEntryTime)}:00`, unit: '' }
      ]
    };
  };

  const generatePatternAnalysisResponse = (personality) => {
    if (!tradingData?.patternAnalysis) {
      return {
        content: `📊 **Pattern Analysis - ${personality.name}**\n\nI need more trading data to perform pattern analysis. Complete at least 15 trades to get comprehensive insights.`,
        insights: { dataAvailable: false },
        recommendations: ['Complete more trades for pattern recognition', 'Track your entry and exit reasons', 'Document your trading setups'],
        dataPoints: []
      };
    }

    const patterns = tradingData.patternAnalysis;
    
    let content = `📊 **Trading Pattern Analysis - ${personality.name}**\n\n`;
    
    if (patterns.highWinRateSymbols.length > 0) {
      content += `✅ **High Win Rate Symbols:**\n`;
      patterns.highWinRateSymbols.forEach(symbol => {
        content += `• ${symbol.symbol}: ${(symbol.winRate * 100).toFixed(1)}% win rate, $${symbol.avgPnL.toFixed(2)} avg\n`;
      });
    }
    
    if (patterns.highLossRateSymbols.length > 0) {
      content += `\n❌ **High Loss Rate Symbols:**\n`;
      patterns.highLossRateSymbols.forEach(symbol => {
        content += `• ${symbol.symbol}: ${(symbol.winRate * 100).toFixed(1)}% win rate, $${symbol.avgPnL.toFixed(2)} avg\n`;
      });
    }

    return {
      content,
      insights: {
        highWinRateSymbols: patterns.highWinRateSymbols,
        highLossRateSymbols: patterns.highLossRateSymbols
      },
      recommendations: [
        'Focus on your high win rate symbols',
        'Avoid or reduce position sizes in problematic symbols',
        'Analyze what makes your successful symbols work',
        'Document your trading patterns for optimization'
      ],
      dataPoints: [
        { label: 'High Win Rate Symbols', value: patterns.highWinRateSymbols.length, unit: '' },
        { label: 'Problem Symbols', value: patterns.highLossRateSymbols.length, unit: '' },
        { label: 'Pattern Recognition', value: 'Active', unit: '' }
      ]
    };
  };

  const generateMarketInsightsResponse = (personality) => {
    if (!tradingData?.marketInsights) {
      return {
        content: `🌍 **Market Insights - ${personality.name}**\n\nI'm still gathering collective market data. This will provide insights from all traders on the platform.`,
        insights: { dataAvailable: false },
        recommendations: ['Check back later for collective insights', 'Focus on your individual performance', 'Track market sentiment'],
        dataPoints: []
      };
    }

    const insights = tradingData.marketInsights;
    
    let content = `🌍 **Collective Market Insights - ${personality.name}**\n\n`;
    content += `**Platform Statistics:**\n`;
    content += `• Overall Win Rate: ${(insights.collectiveWinRate * 100).toFixed(1)}%\n`;
    content += `• Market Sentiment: ${insights.marketSentiment}\n`;
    content += `• Volatility Trend: ${insights.volatilityTrend}\n`;
    
    if (insights.topPerformingSymbols.length > 0) {
      content += `\n**Top Performing Symbols (Collective):**\n`;
      insights.topPerformingSymbols.slice(0, 5).forEach((symbol, index) => {
        content += `${index + 1}. ${symbol.name}: ${symbol.winRate}% win rate\n`;
      });
    }

    return {
      content,
      insights: {
        collectiveWinRate: insights.collectiveWinRate,
        marketSentiment: insights.marketSentiment,
        topSymbols: insights.topPerformingSymbols
      },
      recommendations: [
        'Compare your performance to the collective average',
        'Consider trading the top-performing symbols',
        'Adjust your strategy based on market sentiment',
        'Use collective data to validate your decisions'
      ],
      dataPoints: [
        { label: 'Collective Win Rate', value: (insights.collectiveWinRate * 100).toFixed(1), unit: '%' },
        { label: 'Market Sentiment', value: insights.marketSentiment, unit: '' },
        { label: 'Top Symbols', value: insights.topPerformingSymbols.length, unit: '' }
      ]
    };
  };

  const generatePsychologyResponse = (personality) => {
    const psychologyScore = userPerformance?.psychologyScore || 70;
    const riskLevel = userPerformance?.riskLevel || 'MEDIUM';
    
    let content = `🧠 **Psychology Analysis - ${personality.name}**\n\n`;
    
    if (psychologyScore >= 80) {
      content += `Excellent! Your psychology score of ${psychologyScore}/100 shows strong emotional control. You're in the top 20% of traders.`;
    } else if (psychologyScore >= 60) {
      content += `Good progress! Your psychology score of ${psychologyScore}/100 indicates solid emotional management with room for improvement.`;
    } else {
      content += `Your psychology score of ${psychologyScore}/100 suggests you need to focus on emotional control. This is crucial for consistent performance.`;
    }

    content += `\n\n**Key Insights:**\n`;
    content += `• Your risk tolerance: ${riskLevel}\n`;
    content += `• Emotional stability: ${psychologyScore >= 70 ? 'Strong' : 'Needs work'}\n`;
    content += `• Decision-making: ${psychologyScore >= 75 ? 'Rational' : 'Emotionally influenced'}\n`;

    return {
      content,
      insights: {
        psychologyScore,
        riskLevel,
        emotionalStability: psychologyScore >= 70 ? 'Strong' : 'Needs work'
      },
      recommendations: [
        'Practice daily meditation for 10 minutes',
        'Keep a trading journal to track emotional states',
        'Set strict stop-losses to remove emotion from exits'
      ],
      dataPoints: [
        { label: 'Psychology Score', value: psychologyScore, unit: '/100' },
        { label: 'Risk Level', value: riskLevel, unit: '' },
        { label: 'Emotional Control', value: psychologyScore >= 70 ? 'Good' : 'Improving', unit: '' }
      ]
    };
  };

  const generateRiskResponse = (personality) => {
    const riskLevel = userPerformance?.riskLevel || 'MEDIUM';
    const winRate = userPerformance?.winRate || 0.55;
    
    let content = `🎯 **Risk Management Analysis - ${personality.name}**\n\n`;
    
    if (riskLevel === 'LOW') {
      content += `Your conservative risk approach is protecting your capital well. With a ${(winRate * 100).toFixed(1)}% win rate, you're building steady profits.`;
    } else if (riskLevel === 'MEDIUM') {
      content += `Balanced risk management. Your ${(winRate * 100).toFixed(1)}% win rate suggests good risk-reward optimization.`;
    } else {
      content += `High-risk approach detected. While potentially profitable, ensure your ${(winRate * 100).toFixed(1)}% win rate justifies the risk.`;
    }

    content += `\n\n**Risk Metrics:**\n`;
    content += `• Position sizing: ${riskLevel === 'LOW' ? 'Conservative' : riskLevel === 'MEDIUM' ? 'Balanced' : 'Aggressive'}\n`;
    content += `• Win rate: ${(winRate * 100).toFixed(1)}%\n`;
    content += `• Risk per trade: ${riskLevel === 'LOW' ? '1-2%' : riskLevel === 'MEDIUM' ? '2-3%' : '3-5%'}\n`;

    return {
      content,
      insights: {
        riskLevel,
        winRate,
        positionSizing: riskLevel === 'LOW' ? 'Conservative' : riskLevel === 'MEDIUM' ? 'Balanced' : 'Aggressive'
      },
      recommendations: [
        'Never risk more than 2% of capital per trade',
        'Use proper position sizing based on stop-loss distance',
        'Maintain a 2:1 minimum risk-reward ratio'
      ],
      dataPoints: [
        { label: 'Risk Level', value: riskLevel, unit: '' },
        { label: 'Win Rate', value: (winRate * 100).toFixed(1), unit: '%' },
        { label: 'Position Size', value: riskLevel === 'LOW' ? '1-2%' : riskLevel === 'MEDIUM' ? '2-3%' : '3-5%', unit: 'of capital' }
      ]
    };
  };

  const generateGeneralResponse = (personality, userInput) => {
    let content = `💡 **General Trading Advice - ${personality.name}**\n\n`;
    
    content += `I understand you're asking about: "${userInput}"\n\n`;
    content += `**Key Principles to Remember:**\n`;
    content += `• Always have a plan before entering a trade\n`;
    content += `• Risk management is more important than being right\n`;
    content += `• Consistency beats perfection\n`;
    content += `• Keep learning and adapting\n\n`;
    
    content += `**Personalized Tip:**\n`;
    content += `Based on your trading style, focus on ${personality.description.toLowerCase()}. This aligns with your current performance patterns.`;

    return {
      content,
      insights: {
        adviceType: 'General',
        personalityAlignment: personality.name,
        focusArea: personality.description
      },
      recommendations: [
        'Review your trading plan weekly',
        'Track your emotional state during trades',
        'Analyze your best and worst trades monthly'
      ],
      dataPoints: [
        { label: 'Advice Type', value: 'General', unit: '' },
        { label: 'Personality Match', value: personality.name, unit: '' },
        { label: 'Focus Area', value: personality.description, unit: '' }
      ]
    };
  };

  const toggleInsights = (messageId) => {
    setExpandedInsights(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: isMobile ? '100%' : '80vh',
          maxHeight: isMobile ? '100%' : '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: 'primary.main', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <AIIcon />
        <Typography variant="h6">Enhanced AI Trading Assistant</Typography>
        <IconButton
          onClick={onClose}
          sx={{ color: 'white', ml: 'auto' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* AI Personality Selector */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Choose AI Personality:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {Object.entries(aiPersonalities).map(([key, personality]) => (
              <Chip
                key={key}
                icon={personality.icon}
                label={personality.name}
                color={aiPersonality === key ? personality.color : 'default'}
                variant={aiPersonality === key ? 'filled' : 'outlined'}
                onClick={() => setAiPersonality(key)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Box>

        {/* Messages */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>
          {messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                mb: 2
              }}
            >
              <Paper
                elevation={2}
                sx={{
                  maxWidth: '80%',
                  p: 2,
                  bgcolor: message.type === 'user' ? 'primary.main' : 'grey.50',
                  color: message.type === 'user' ? 'white' : 'text.primary',
                  borderRadius: 2,
                  position: 'relative'
                }}
              >
                {/* Message Header */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  mb: 1 
                }}>
                  {message.type === 'ai' ? (
                    <Avatar sx={{ bgcolor: 'secondary.main', width: 24, height: 24 }}>
                      <AIIcon fontSize="small" />
                    </Avatar>
                  ) : (
                    <Avatar sx={{ bgcolor: 'primary.main', width: 24, height: 24 }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                  )}
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {message.type === 'ai' ? aiPersonalities[aiPersonality].name : 'You'}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.6, ml: 'auto' }}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>

                {/* Message Content */}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    whiteSpace: 'pre-line',
                    lineHeight: 1.6
                  }}
                >
                  {message.content}
                </Typography>

                {/* AI Insights and Data */}
                {message.type === 'ai' && message.insights && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      size="small"
                      onClick={() => toggleInsights(message.id)}
                      endIcon={expandedInsights[message.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{ mb: 1 }}
                    >
                      {expandedInsights[message.id] ? 'Hide Insights' : 'Show Insights'}
                    </Button>

                    <Collapse in={expandedInsights[message.id]}>
                      <Card variant="outlined" sx={{ mt: 1 }}>
                        <CardContent sx={{ p: 2 }}>
                          {/* Data Points */}
                          {message.dataPoints && message.dataPoints.length > 0 && (
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              {message.dataPoints.map((point, index) => (
                                <Grid item xs={12} sm={4} key={index}> {/* Fixed: Changed sm=4 to sm={4} */}
                                  <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h6" color="primary">
                                      {point.value}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {point.label}
                                    </Typography>
                                    {point.unit && (
                                      <Typography variant="caption" color="text.secondary">
                                        {point.unit}
                                      </Typography>
                                    )}
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          )}

                          {/* Recommendations */}
                          {message.recommendations && message.recommendations.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LightbulbIcon fontSize="small" color="warning" />
                                Recommendations
                              </Typography>
                              <List dense>
                                {message.recommendations.map((rec, index) => (
                                  <ListItem key={index} sx={{ py: 0.5 }}>
                                    <ListItemText 
                                      primary={rec}
                                      primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Collapse>
                  </Box>
                )}
              </Paper>
            </Box>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
              <Paper elevation={2} sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    AI is analyzing your data and learning from collective insights...
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Error message */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box component="form" onSubmit={handleSendMessage} sx={{ display: 'flex', gap: 1 }}>
            <TextField
              ref={inputRef}
              fullWidth
              multiline
              maxRows={3}
              placeholder="Ask me about your profitable times, best trades, patterns, market insights, or psychology..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              variant="outlined"
              size="small"
            />
            <Button
              type="submit"
              variant="contained"
              disabled={!inputMessage.trim() || isLoading}
              sx={{ minWidth: 56 }}
            >
              <SendIcon />
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EnhancedAITradingChat;
